import { useRef, useState, type DragEvent, type ReactNode } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";

const isCsvFile = (file: File): boolean => {
  if (file.name.toLowerCase().endsWith(".csv")) return true;
  const type = file.type.toLowerCase();
  return (
    type === "text/csv" ||
    type === "application/csv" ||
    type === "application/vnd.ms-excel"
  );
};

const Zone = styled.div<{ $active: boolean }>`
  position: relative;
  border-radius: ${borderRadius.large}px;
  width: 100%;
  box-sizing: border-box;
  min-height: calc(100vh - 64px - 200px);
  min-height: calc(100dvh - 64px - 200px);

  ${({ $active }) =>
    $active
      ? css`
          box-shadow: 0 0 0 3px ${colors.primary};
          outline: 2px dashed ${colors.primary};
          outline-offset: 2px;
        `
      : ""}
`;

const Overlay = styled.div<{ $visible: boolean }>`
  display: ${({ $visible }) => ($visible ? "flex" : "none")};
  position: absolute;
  inset: 0;
  z-index: 20;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  border-radius: inherit;
  background-color: hsla(220, 85%, 7%, 0.06);
`;

const OverlayText = styled.p`
  margin: 0;
  padding: ${spacing.x3}px ${spacing.x4}px;
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.primary};
  text-align: center;
`;

type ImportDropZoneProps = {
  children: ReactNode;
  onFile: (file: File) => void;
};

export const ImportDropZone = ({ children, onFile }: ImportDropZoneProps) => {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.dataTransfer.types.includes("Files")) return;
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && isCsvFile(file)) {
      onFile(file);
    }
  };

  return (
    <Zone
      $active={isDragging}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Overlay $visible={isDragging}>
        <OverlayText>{t("import.dropZoneHint")}</OverlayText>
      </Overlay>
      {children}
    </Zone>
  );
};
